import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { AbsolutePopup, PopPaper } from 'core-components/library/popup'
import { IDataProviderFilter, IFilter, Logic } from 'core-components/types'

import { equalFilters } from '../helpers/equalFilters'

import { FilterLine } from './FilterLine'
import { IFilterAreaProps } from './models/IFilterAreaProps'

import styles from './FilterArea.module.scss'

export function FilterArea<T>({
  className,
  autoPopupWidth,
  source,
  ignoredFields,
  PanelContent,
  onFilterRender,
  ignoredDefaultFields,
  presetFilters,
}: IFilterAreaProps<T>) {
  const [filter, setFilter] = useState<IDataProviderFilter>()
  const [defaultFilter, setDefaultFilter] = useState<IDataProviderFilter>()
  const [unsavedFilter, setUnsavedFilter] = useState<IDataProviderFilter | undefined>(undefined)
  const sourceCurrentFilter = source.getFilter()
  const panelContentFilter = useMemo(() => {
    if (unsavedFilter) {
      return unsavedFilter
    }

    return sourceCurrentFilter ? JSON.parse(JSON.stringify(sourceCurrentFilter)) : undefined
  }, [unsavedFilter, sourceCurrentFilter])

  const eventCallbacks = useMemo(() => {
    return {
      onClose: () => {
        if (filter) {
          source.setFilter(filter)
        }
      },
    }
  }, [source, filter])

  useEffect(() => {
    const f = source.getFilter()
    setFilter(f && { ...f })
    const defFilter = source.getDefaultFilter()
    setDefaultFilter(defFilter && { ...defFilter })
    setUnsavedFilter(undefined)

    return source.subscribe('change', () => {
      const f = source.getFilter()
      setFilter(f && { ...f })
      const defFilter = source.getDefaultFilter()
      setDefaultFilter(defFilter && { ...defFilter })
    })
  }, [source])

  const popup = () => {
    return (
      <AbsolutePopup
        eventCallbacks={eventCallbacks}
        popupContent={(popupData) => (
          <PopPaper
            className={classNames(styles.popup, autoPopupWidth ? '' : styles.defaultPopupWidth)}
          >
            <div className={styles.buttons}>
              <Button
                className={styles.buttons__item}
                onClick={() => {
                  if (unsavedFilter) {
                    setFilter(unsavedFilter)
                    source.setFilter(unsavedFilter)
                    setUnsavedFilter(undefined)
                  }
                  popupData.action.close()
                }}
              >
                Apply
              </Button>
              <Button
                className={styles.buttons__item}
                variant="text"
                color={'danger'}
                onClick={() => {
                  popupData.action.close()
                }}
              >
                Cancel
              </Button>
            </div>
            <PanelContent
              filter={panelContentFilter}
              onFilterChange={(value: IDataProviderFilter) => {
                setUnsavedFilter(value)
              }}
            />
          </PopPaper>
        )}
      >
        <Button
          className={classNames('filter-menu')}
          variant="text"
          iconButton={'kbase kbase-add'}
        />
      </AbsolutePopup>
    )
  }
  const filters = source.getFilter()
  const presetFiltersState =
    presetFilters?.map((el) => {
      return !!filters?.filters.some((value) => {
        const existingFilter = value as IFilter
        return equalFilters(existingFilter, el.filter)
      })
    }) || []

  return (
    <div className={classNames(styles.root, className || '')}>
      <div className={styles.header}>
        <p className={styles.header__title}>Filter:</p>
        {defaultFilter ? (
          <FilterLine
            filter={defaultFilter}
            handleRemoveField={() => {}}
            onFilterRender={onFilterRender}
            ignoredFields={ignoredDefaultFields}
          />
        ) : null}
        {filter ? (
          <FilterLine
            filter={filter}
            ignoredFields={ignoredFields}
            handleRemoveField={(newFilter: IDataProviderFilter) => {
              setFilter({ ...newFilter })
              source.setFilter(newFilter)
            }}
            onFilterRender={onFilterRender}
          />
        ) : null}
        {!filter && !defaultFilter ? 'Empty' : null}
        {popup()}
      </div>
      {presetFilters && presetFilters.length && (
        <div className={styles.presetFiltersContainer}>
          {presetFilters.map((el, key) => {
            return (
              <div key={key} className={styles.presetFilter}>
                <Checkbox
                  checked={presetFiltersState[key]}
                  onChange={(value) => {
                    if (!value) {
                      const currentFilter = filter || {
                        logic: Logic.AND,
                        filters: [],
                      }
                      const newFilter = {
                        ...currentFilter,
                        filters: currentFilter.filters.filter((existingFilter) => {
                          return !equalFilters(existingFilter as IFilter, el.filter)
                        }),
                      }
                      source.setFilter(newFilter)
                      setFilter(newFilter)
                    } else {
                      const currentFilter = filter || {
                        logic: Logic.AND,
                        filters: [],
                      }
                      const newFilter = {
                        ...currentFilter,
                        filters: [...currentFilter.filters, el.filter],
                      }
                      source.setFilter(newFilter)
                      setFilter(newFilter)
                    }
                  }}
                />
                <div>{el.label}</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
